import { collectionChangedFilter, CurrencyCollectionQuery, propertyChangedFilter, propertyChangedJoinPointFilter } from '@skylab-shared/business';
import { BusinessObjectPart, BusinessObjectPartContext, CollectionChangedContext, ModelBase, ObservableCollection, PropertyChangedJoinPoint, Weaver } from '@skylab-shared/core';
import { OrderPaymentModel, OrderPaymentSummary } from '@skylab-shared/dataModel';
import { Calc, MidpointRounding } from '@skylab-shared/utils';

interface DocumentModelPart extends ModelBase {
	grossValue: number;
	currencyId: string;
	paymentSummary: OrderPaymentSummary,
	payments: ObservableCollection<OrderPaymentModel>
}

export class OrderPaymentsCalculatorBoPart extends BusinessObjectPart<DocumentModelPart> {
	public constructor(
		weaver: Weaver,
	) {
		super(weaver);
	}

	public override async initializeAsync(context: BusinessObjectPartContext<DocumentModelPart>): Promise<void> {
		await super.initializeAsync(context);
		this.registerRecalculationFeatures();
	}

	private registerRecalculationFeatures(): void {
		this.coreFeature.collectionChanged.listen<DocumentModelPart>(ctx => onItemsCollectionChanged(ctx as unknown as CollectionChangedContext<DocumentModelPart>));
		const onItemsCollectionChanged = collectionChangedFilter(
			this.modelType,
			'payments',
			() => this.recalculatePaymentSummary()
		);

		this.coreFeature.propertyChanged.listen<DocumentModelPart>(propertyChangedFilter(
			this.modelType,
			['payments'],
			() => this.recalculatePaymentSummary()
		));

		this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(OrderPaymentModel, ['amount'])(
			ctx => this.recalculatePaymentSummary()
		)));

		this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['grossValue'])(
			ctx => this.recalculatePaymentSummary()
		)));
	}

	private recalculatePaymentSummary(): void {
		this.bo.model.paymentSummary.totalAmount = this.bo.model.grossValue;
		this.bo.model.paymentSummary.paidAmount = this.paidAmount();
		this.bo.model.paymentSummary.pendingAmount = this.pendingAmount(this.bo.model.paymentSummary.paidAmount);
	}

    public pendingAmount(paid: number): number {
        const { precision } = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { externalId: this.bo.model.currencyId });
        const remaining = Calc.round(Math.abs(this.bo.model.grossValue) - paid, precision, MidpointRounding.AwayFromZero);

        return remaining;
    }

    public paidAmount(): number {
        if (!this.bo.model.currencyId) {
            return 0;
        }

        const { precision } = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { externalId: this.bo.model.currencyId });
        const paid = Calc.round(this.bo.model.payments.reduce((sum, { amount }) => sum + amount, 0), precision, MidpointRounding.AwayFromZero);

        return paid;
    }
}
